import AuthMixin from '@/components/mixins/auth-mixin'
import EventBus from '@/components/mixins/event-bus-mixin'
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin'
import Draggable from 'vuedraggable'
import MediaList from '../../common/media-list/media-list-tmpl'
import AudioPlayer from '../../common/audio-player/audio-player-tmpl'
import ProjectAudioMenu from './project-audio-menu/project-audio-menu-tmpl'
import MediaDetailsMenu from '@/components/common/media-menu/media-menu-tmpl'
import CONST from '@/globals/constants'
import { scheduleStore } from '@/pinia/stores/schedule/scheduleStore'
import { mapActions, mapState as piniaMapState } from 'pinia'
import { useFileUploaderStore } from '@/pinia/stores/file-upload/fileUploaderStore'
import { useMixpanel } from '@base-shared/composables/use-mixpanel'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth'

import _ from 'lodash'

export default {
  name: 'createAudio',
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$api',
    '$apiError'
  ],
  components: {
    Draggable,
    MediaList,
    AudioPlayer,
    ProjectAudioMenu,
    MediaDetailsMenu
  },
  props: {
    routeNameProp: {
      type: String,
      default () { return '' }
    }
  },
  computed: {
    ...piniaMapState(useFileUploaderStore, {
      fileUploaderState: 'uploadedFiles'
    }),
    ...piniaMapState(scheduleStore, {
      scheduleStoreIsFetching: 'isFetching'
    }),
    userSchedulesDoneFetching () {
      return !this.scheduleStoreIsFetching;
    },
    sortDraggableOptions () {
      return { animation: 250, group: 'description', disabled: false, ghostClass: 'ghost' }
    }
  },
  watch: {
    scheduleStoreIsFetching (newVal) {
      this.$utils.mainLoader(newVal)
    },
    fileUploaderState (newVal) {
      if (newVal.prevAction === 'processNewFilesWithInstance') {
        const data = []
        newVal.files.forEach(file => {
          if (file._edit && file._edit.uploadRequests.createMediaInstanceResponseData) data.push(file._edit.uploadRequests.createMediaInstanceResponseData)
        })
        this.handleMediaListChange({ action: 'UPLOADED_MEDIA_FILES', payload: { data } })
      }
    },
    routeNameProp (newVal) {
      this.routeName = newVal
      this.setDefaultData()
    },
    projectFrameStatus (newVal) {
      this.handleSubmitButtonText(newVal)
    },
    projectAudioPlaylist (newVal) {
      const mediaList = []
      if (newVal && (newVal.data && newVal.data.audio)) {
        newVal.data.audio.forEach(audio => {
          if (audio.preview) {
            audio.type = 'svg'
            mediaList.push(audio)
          }
        })
      }
    }
  },
  mixins: [AuthMixin, UnsavedDataMixin],
  data () {
    return {
      CONST,
      mediaRowActions: {
        rowSelected: 'rowSelected',
        deleteRow: 'deleteRow',
        duplicateRow: 'duplicateRow',
        playAudioPreview: 'playAudioPreview',
        pauseAudioPreview: 'pauseAudioPreview',
        updateAudioPreviewCurrentSong: 'updateAudioPreviewCurrentSong',
        resetAudioPlayer: 'resetAudioPlayer'
      },
      selectedMediaRowUIDs: [],
      previousRowSelected: { row: {}, position: 0 },
      previousKeyPressedInRowSelection: null,
      audio: [],
      audioListBeforeSortMoved: [],
      audioItemSortDragTriggered: false,
      mediaListDialog: {
        isVisible: false,
        menuItemId: '',
        filterType: '',
        filterTypeOptions: ['audio'],
        mediaSelection: []
      },
      enableScheduleSetTarget: this.$currentUser.getRights().userSchedule.setTarget,
      error: '',
      currentScreenIndex: 0, // Current Page Number or called as currentScreenIndex
      projectTemplate: {},
      projectAudioPlaylist: {},
      projectAudioPlaylistAction: CONST.PROJECT_CREATE_AUDIO_PLAYLIST,
      projectFrameStatusOptions: { EDIT_FRAME: 'EDIT_FRAME', PLAY_FRAME: 'PLAY_FRAME' },
      projectFrameStatus: 'EDIT_FRAME',
      projectMediaDetails: null,
      projectPreview: {
        isPlaying: false,
        currentSong: {}
      },
      projectOrientation: this.$dataStore.getMediaOrientation() ? this.$dataStore.getMediaOrientation() : 'horizontal',
      selectedEditFrameTab: null,
      routeName: this.routeNameProp,
      userSchedules: [],
      submitButtonText: 'NEXT',
      menuItemInProgressRequests: [],
      menuItemTabIds: { addApps: 'addApps', settings: 'settings', projectSchedule: 'projectSchedule' },
      addAppsMenuItemsTab: [],
      addAppsTabIconVisible: false,
      addAppsTabMenuItems: [],
      tagsSummaryForSuggestions: [],
      updateAudioPlayer: {
        ref: null,
        action: null,
        data: {}
      },
      hoverAllDeleteIcon: false,
      enableMediaUpload: true,
      uploadingMedia: [],
      audioPlayerData: [],
      headerAudioPreview: null,
      overAllPlaylistDuration: 0
    }
  },
  methods: {
    ...mapActions(scheduleStore, ['fetchAllSchedules']),
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('create-audio-change', { action, payload: { data } })
      }
    },
    /**
     * Set Project Template Action and Frame status
     */
    setDefaultData () {
      const routeName = this.routeName
      const routeParams = this.$route.params

      // Reset Current Frame Page Number
      const createNormalProjectPage = (routeName === 'audio' || (this.$route.params && (this.$route.params.pageContent && this.$route.params.pageContent === 'audio')))
      const editProjectPage = (routeName === 'edit-project-audio')

      // If this Component use for Create Project Page
      if (createNormalProjectPage) {
        this.projectAudioPlaylistAction = CONST.PROJECT_CREATE_AUDIO_PLAYLIST
        this.selectedEditFrameTab = this.menuItemTabIds.settings

        const setId = 'default'
        const playlistId = 'audio-playlist'

        // Get Project Details
        const nextAction = {
          callback: (responseData) => {
            this.getTagsSummary({ closeLoader: true })

            // Set Playlist preview
            this.headerAudioPreview = this.filterHeaderAudioPreview({ type: responseData.preview.type, url: responseData.preview.url })
          }
        }
        this.getProjectAudioPlaylist('newProject', { setId, playlistId, projectSchedule: 'projectSchedule' }, nextAction)
        this.audioPlayerData = []
      }

      // If this Component use for Edit Project Page
      if (editProjectPage) {
        this.projectAudioPlaylistAction = CONST.PROJECT_EDIT_AUDIO_PLAYLIST

        // Get Project Template Details and Project Media Details
        const nextAction = {
          callback: (responseData) => {
            this.getProjectMediaDetails(this.$route.params.projectId, false, () => {
            // Set Project Frame Status
              if (routeParams.projectFrameAction && (routeParams.projectFrameAction === 'edit-frame')) {
                this.projectFrameStatus = this.projectFrameStatusOptions.EDIT_FRAME
                this.selectedEditFrameTab = this.menuItemTabIds.settings
                this.audioPlayerData = []
              } else {
                this.projectFrameStatus = this.projectFrameStatusOptions.PLAY_FRAME
                this.$utils.updatePageComponentStyle({ name: 'createEditProjectAudioPage', playAudio: true })
                this.audioPlayerData = this.projectAudioPlaylist.data.audio
              }
            })
            this.getTagsSummary()

            // Set Playlist preview
            this.headerAudioPreview = this.filterHeaderAudioPreview({ type: responseData.preview.type, url: responseData.preview.url })
          }
        }
        this.getProjectAudioPlaylist('existingProject', { projectId: this.$route.params.projectId }, nextAction)
        // Remove unsaved data flag
        this.removeUnsavedDataFlagInLocalStorage()
      }

      // Set Project Template Orientation
      this.projectOrientation = this.$dataStore.getMediaOrientation()

      // Auto Adjust component style
      this.$nextTick(_ => { this.$utils.updatePageComponentStyle({ name: 'createEditProjectAudioPage', playAudio: this.projectFrameStatus === this.projectFrameStatusOptions.PLAY_FRAME }) })
    },
    /**
     * This will get project media details from API
     * @param {String} resourceId - project resourceId
     */
    getProjectMediaDetails (resourceId, triggerMediaMenuPropertyChange, nextAction) {
      const apiRequest = () => {
        this.$utils.mainLoader(true)
        const params = {
          use_qsa: 1
        }

        this.$api.get(`${CONFIG.API_BASE_URL}/media/${resourceId}`, { params })
          .then(async (response) => {
            this.projectMediaDetails = response.data

            // Make sure to get back schedule data changes after switching tabs
            if (triggerMediaMenuPropertyChange) {
              this.$nextTick(_ => {
                if (this.$refs.projectDetailsMenu) {
                  this.$refs.projectDetailsMenu.handlePropertyChange('schedules')
                }
              })
            }

            await this.getUserSchedules();
            if (nextAction) { nextAction() }
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
          })
      }
      apiRequest()
    },
    async getUserSchedules () {
      const { success, data: schedules } = await this.fetchAllSchedules(false);

      if (!success) {
        EventBus.$emit(
          'SHOW_CUSTOM_NOTIFICATION',
          {
            type: 'error',
            alert: {
              message: this.$t('errors.unexpectedError')
            },
            closeButtonText: this.$t('buttons.reload'),
            callbacks: {
              eventOnClose: () => window.location.reload()
            }
          }
        );

        return;
      }

      this.userSchedules = schedules
    },
    /**
     * This will get Project details from API
     * @param {String} reqType
     * @param {Object} params
     * @param {Object} nextAction - a process once the request is done
     */
    getProjectAudioPlaylist (reqType, params, nextAction) {
      let endpoint = ''
      const queryParams = {
        use_qsa: 1,
        locale: this.$t?.locale?.value ?? 'en'
      }

      if (reqType === 'newProject') {
        endpoint = `${CONFIG.API_BASE_URL}/template/${params.setId}/${params.playlistId}`
      } else if (this.projectAudioPlaylistAction === CONST.PROJECT_EDIT_AUDIO_PLAYLIST) {
        endpoint = `${CONFIG.API_BASE_URL}/project/${params.projectId}`
      }

      const apiRequest = () => {
        this.$utils.mainLoader(true)
        this.$api.get(endpoint, { params: queryParams })
          .then(response => {
            this.projectAudioPlaylist = this.filterProjectAudioPlaylistObject(response.data)

            if (typeof (nextAction) === 'object') {
              nextAction.callback(response.data)
            } else {
              this.$utils.mainLoader(false)
            }
          })
          .catch((error) => {
            this.error = error.message
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { apiRequest() }, showDialogNotification: true })
          })
      }
      apiRequest()
    },
    filterHeaderAudioPreview (headerAudioPreviewSrc) {
      const headerAudioPreview = this.$utils.clone(headerAudioPreviewSrc)

      if (!headerAudioPreview._edit) {
        headerAudioPreview._edit = {
          uid: this.$utils.generateUniqueID(),
          iFrameStatus: 'default',
          preview: headerAudioPreviewSrc
        }
      }

      return headerAudioPreview
    },
    filterProjectAudioPlaylistObject (filterProjectAudioPlaylistSrc) {
      const projectAudioPlaylist = this.$utils.clone(filterProjectAudioPlaylistSrc)

      if (!projectAudioPlaylist._edit) {
        projectAudioPlaylist._edit = {}
      }

      // Add uid in data pages
      if (projectAudioPlaylist.data && projectAudioPlaylist.data.audio) {
        const backgroundColorOptions = ['#9adef7']

        this.overAllPlaylistDuration = 0
        for (const audioIndx in projectAudioPlaylist.data.audio) {
          const audioItem = projectAudioPlaylist.data.audio[audioIndx]

          if (!audioItem.uid) {
            audioItem.uid = `${this.$utils.generateUniqueID()}-${audioIndx}`
          }

          if (!audioItem.order) {
            const topSiblingDetails = (projectAudioPlaylist.data.audio[audioIndx - 1]) ? projectAudioPlaylist.data.audio[audioIndx - 1] : false
            const bottomSiblingDetails = (projectAudioPlaylist.data.audio[audioIndx + 1]) ? projectAudioPlaylist.data.audio[audioIndx + 1] : false
            const topSiblingOrder = topSiblingDetails ? topSiblingDetails.order : false
            const bottomSiblingOrder = bottomSiblingDetails ? bottomSiblingDetails.order : false
            audioItem.order = this.$utils.calculateSortOrderValue({ topSiblingOrder, bottomSiblingOrder, previousOrderVal: 0 })
          }

          if (!audioItem._edit) {
            audioItem._edit = {
              isRowSelected: false,
              sortKeyWithIndx: `${audioItem.order}_${audioIndx}`,
              textLabel: audioItem.name
            }
          }

          // Filter preview
          if (audioItem.preview && audioItem.preview.url) {
            audioItem.preview.url = this.$utils.filterMediaPreviewUrl(audioItem.preview.url, audioItem)
          }

          // Ajax for iframe
          audioItem._edit.uid = `audioPagePreview_${audioItem.uid}`
          audioItem._edit.iFrameStatus = 'default'

          // Set row text label
          if (audioItem.metadata) {
            if (audioItem.metadata.title) {
              audioItem._edit.textLabel = audioItem.metadata.title
            }

            if (audioItem.metadata.album) {
              audioItem._edit.textLabel = `${audioItem._edit.textLabel}, ${audioItem.metadata.album}`
            }

            if (audioItem.metadata.artist) {
              audioItem._edit.textLabel = `${audioItem._edit.textLabel}, ${audioItem.metadata.artist}`
            }
          }

          audioItem._edit.backgroundColor = backgroundColorOptions[audioIndx % backgroundColorOptions.length]

          audioItem._edit.isRowSelected = false
          if (this.selectedMediaRowUIDs.indexOf(audioItem.uid) >= 0) {
            audioItem._edit.isRowSelected = true
          }

          // Update overall duratoin
          if (audioItem.dur) this.overAllPlaylistDuration += audioItem.dur
        }

        // Forma overall duration
        this.overAllPlaylistDuration = this.$utils.formatDurationToMinutesAndSeconds({ duration: this.overAllPlaylistDuration, forAudioPlayer: true })
      }

      return projectAudioPlaylist
    },
    /**
     * This will get project media details from API
     * @param {String} resourceId - project resourceId
     */
    getTagsSummary (params) {
      if (!params) params = {}

      const apiRequest = () => {
        let endPoint = `${CONFIG.API_BASE_URL}`
        endPoint += '/group/tags/?type=resourceTags,playerTags,styleTags&ext=1'
        this.$api.get(`${endPoint}`)
          .then(response => {
            const responseData = response.data
            const tagsSummary = []

            for (const tagType in responseData) {
              responseData[tagType].forEach((tag) => {
                tag.value = tag.name
                tagsSummary.push(tag)
              })
            }

            this.tagsSummaryForSuggestions = this.$utils.filterTagOptionsForUI({ tags: tagsSummary })

            if (params.closeLoader) this.$utils.mainLoader(false)
          })
          .catch((error) => {
            this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
          })
      }
      apiRequest()
    },
    /**
     * Handle Submit Button Text
     * @param {String} - projectFrameStatus
     */
    handleSubmitButtonText (projectFrameStatus) {
      if (projectFrameStatus === this.projectFrameStatusOptions.EDIT_FRAME) {
        this.submitButtonText = this.$t('label.next')
      } else {
        this.submitButtonText = this.$t('label.ok')
      }
    },
    handleAudioItemSortStart () {
      this.setIsPlaylistChanged(true)
      this.audioItemSortDragTriggered = true
      this.audioListBeforeSortMoved = this.$utils.clone(this.projectAudioPlaylist.data.audio)
      EventBus.$emit('SORTING_START', { audioPage: true })
    },
    handleAudioItemSortEnd (sortEvtVal) {
      this.audioItemSortDragTriggered = false
      EventBus.$emit('SORTING_END', { audioPage: true })

      // Previous Data
      const audioListBeforeSort = this.$utils.clone(this.audioListBeforeSortMoved)
      const previousIndexInList = sortEvtVal.oldIndex
      const previousOrderVal = audioListBeforeSort[previousIndexInList].order

      const projectAudioPlaylist = this.$utils.clone(this.projectAudioPlaylist)
      const currentIndexInList = sortEvtVal.newIndex
      const topSiblingDetails = (projectAudioPlaylist.data.audio[currentIndexInList - 1]) ? projectAudioPlaylist.data.audio[currentIndexInList - 1] : false
      const bottomSiblingDetails = (projectAudioPlaylist.data.audio[currentIndexInList + 1]) ? projectAudioPlaylist.data.audio[currentIndexInList + 1] : false
      const topSiblingOrder = topSiblingDetails ? topSiblingDetails.order : false
      const bottomSiblingOrder = bottomSiblingDetails ? bottomSiblingDetails.order : false

      projectAudioPlaylist.data.audio[currentIndexInList].order = this.$utils.calculateSortOrderValue({ topSiblingOrder, bottomSiblingOrder, previousOrderVal })

      this.projectAudioPlaylist = projectAudioPlaylist
    },
    addAudioIconClick () {
      useMixpanel().track('Add media in Create Audio')
      const mediaListDialog = this.$utils.clone(this.mediaListDialog)
      mediaListDialog.filterType = 'all'
      mediaListDialog.isVisible = true
      mediaListDialog.mediaSelection = []
      mediaListDialog.mediaItemToHide = []
      this.mediaListDialog = mediaListDialog
    },
    handleMediaListChange ($event) {
      this.setIsPlaylistChanged(true)
      const projectAudioPlaylist = this.$utils.clone(this.projectAudioPlaylist)
      const newMediaActions = ['MEDIA_SELECTION', 'UPLOADED_MEDIA_FILES']

      if (newMediaActions.indexOf($event.action) >= 0) {
        const newAddedMedia = $event.payload.data.filter((item) => {
          if (item._edit) { delete item._edit }
          return this.uploadingMedia.indexOf(item.resourceId) < 0
        })
        const currentAudioList = projectAudioPlaylist.data.audio

        // Add into uploading media list if uploding and Update current audio if already exist(It happens when user replace the file during upload in file uploader)
        newAddedMedia.forEach((newMedia, newMediaIndx) => {
          if (!newMedia.audioPreview) this.uploadingMedia.push(newMedia.resourceId)

          currentAudioList.forEach((audio, indx) => {
            if (audio.resourceId === newMedia.resourceId) {
              currentAudioList[indx].audioPreview = newMedia.audioPreview ? newMedia.audioPreview : ''
              currentAudioList[indx].dur = newMedia.dur ? newMedia.dur : 0

              if (newMedia.preview && !newMedia.preview.status) {
                currentAudioList[indx].preview = newMedia.preview
              }
            }
          })
        })

        projectAudioPlaylist.data.audio = currentAudioList.concat(newAddedMedia)
      }

      this.projectAudioPlaylist = this.filterProjectAudioPlaylistObject(projectAudioPlaylist)
      this.mediaListDialog.isVisible = false
    },
    handleDialogClose () {
      this.mediaListDialog.isVisible = false
      EventBus.$emit('MEDIA_PICKER_CLOSED')
    },
    handleEditTemplateMenuItemsTabIcon (tabIcon) {
      // If settings tab icon is selected then update frame status
      if (tabIcon === this.menuItemTabIds.settings) {
        // Remove unsaved data flag when going back to settings tab
        this.removeUnsavedDataFlagInLocalStorage()
        this.projectFrameStatus = this.projectFrameStatusOptions.EDIT_FRAME
        this.pagesLoaded = []
        this.audioPlayerData = []
        this.$nextTick(_ => { this.$utils.updatePageComponentStyle({ name: 'createEditProjectAudioPage', playAudio: false }) })

        // Replace route to edit frame
        if (this.$route.params.projectFrameAction === 'play-frame') {
          this.$router.replace(`/project-audio/${this.$route.params.projectId}/edit-frame`)
        }

        this.resetAudioPlayerAction()
      }

      // If schedule is selected then set the frame to preview and submit the data
      if (tabIcon === this.menuItemTabIds.projectSchedule) {
        if ((this.projectFrameStatus === this.projectFrameStatusOptions.EDIT_FRAME) &&
          ([CONST.PROJECT_CREATE_AUDIO_PLAYLIST, CONST.PROJECT_EDIT_AUDIO_PLAYLIST].includes(this.projectAudioPlaylistAction))) {
          this.handleProjectSubmitButtonClick('submitProject', 'mediaTabIcon')
        }
      }

      this.selectedEditFrameTab = tabIcon
    },
    redirectToMediaPage () {
      if (this.$router) {
        this.$router.push('/media', () => {})
      }
    },
    handleProjectSubmitButtonClick (action) {
      this.setIsPlaylistChanged(false)
      let requestData = this.$utils.clone(this.projectAudioPlaylist)
      const projectId = requestData.resourceId ? requestData.resourceId : null
      requestData.type = 'audio-playlist'

      delete requestData._edit

      if (!requestData.data || !requestData.data.audio.length) return

      if (this.uploadingMedia.length) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'warning',
          title: this.$t('label.warning'),
          alert: {
            message: this.$t('texts.waitForUploadingFile')
          },
          closeButtonText: this.$t('buttons.ok'),
          callbacks: {
            eventOnClose: () => {
              return false
            }
          }
        })
        return
      }

      this.resetAudioPlayerAction() // Reset Audio Player

      // Create Project Template Request
      if (this.projectAudioPlaylistAction === CONST.PROJECT_CREATE_AUDIO_PLAYLIST) {
        this.$utils.mainLoader(true)

        const apiRequest = () => {
          requestData = this.$utils.filterProjectDataForBackend({ project: requestData })
          this.$api.post(`${CONFIG.API_BASE_URL}/project`, requestData)
            .then(response => {
              this.removeUnsavedDataFlagInLocalStorage() // Remove unsaved data flag

              if (this.$router) {
                this.$router.push(`/project-audio/${response.data.resourceId}/play-frame`, () => {})
                this.$utils.updatePageComponentStyle({ name: 'createEditProjectAudioPage', playAudio: true })
              }
            })
            .catch((error) => {
              this.$utils.mainLoader(false)
              this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
            })
        }
        apiRequest()
      }

      // Edit Project Template request
      if (this.projectAudioPlaylistAction === CONST.PROJECT_EDIT_AUDIO_PLAYLIST) {
        const projectMediaDetails = this.$utils.clone(this.projectMediaDetails)
        let requestData = this.$utils.clone(this.projectAudioPlaylist)

        if (requestData._edit) delete requestData._edit

        // Only project data is submitted
        if (action && action === 'submitProject') {
          const apiRequest = () => {
            this.$utils.mainLoader(true)

            requestData = this.$utils.filterProjectDataForBackend({ project: requestData })
            this.$api.put(`${CONFIG.API_BASE_URL}/project/${projectId}`, requestData)
              .then(response => {
                // Remove unsaved data flag and update frame status
                this.removeUnsavedDataFlagInLocalStorage()
                const projectId = this.$route.params.projectId
                this.getProjectMediaDetails(projectId, true, () => {
                  this.projectFrameStatus = this.projectFrameStatusOptions.PLAY_FRAME

                  // Replace route to play frame
                  this.$router.replace(`/project-audio/${this.$route.params.projectId}/play-frame`)
                  this.$utils.updatePageComponentStyle({ name: 'createEditProjectAudioPage', playAudio: true })
                  this.audioPlayerData = this.projectAudioPlaylist.data.audio
                })
              })
              .catch((error) => {
                this.$utils.mainLoader(false)
                this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
              })
          }
          apiRequest()
        }

        // Only media data is submitted
        if (typeof (action) === 'string' && action === 'submitMedia') {
          const submitMediaData = (this.enableScheduleSetTarget) || (!this.enableScheduleSetTarget && this.$currentUser.hasGroupUserAlert())
          if (submitMediaData) {
            const apiRequest = () => {
              this.$utils.mainLoader(true)

              // Do not send project data for edit media
              if (projectMediaDetails.data) delete projectMediaDetails.data

              // Update Alert schedule
              if (projectMediaDetails.alertSubmitBtn) {
                if (projectMediaDetails.schedules && projectMediaDetails.schedules.length) {
                  for (const schedule of projectMediaDetails.schedules) {
                    schedule.enabled = true
                  }

                  if (projectMediaDetails.alertSubmitBtn === 'reactivate') {
                    for (const schedule of projectMediaDetails.schedules) {
                      schedule.enabled = true
                    }
                  }
                }
              }

              // Do not send tags.Media tags will be updated by the backend after project updates
              if (projectMediaDetails.tags) delete projectMediaDetails.tags

              this.$api.put(`${CONFIG.API_BASE_URL}/media/${projectId}`, projectMediaDetails)
                .then(response => {
                  this.$utils.mainLoader(false)
                  this.removeUnsavedDataFlagInLocalStorage()
                  this.redirectToMediaPage()
                })
                .catch((error) => {
                  this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
                  this.$utils.mainLoader(false)
                })
            }
            apiRequest()
          } else {
            this.redirectToMediaPage()
          }
        }
      }
    },
    handleMediaRowClick (action, audio, indx, eventDom) {
      /* Select Row */
      if (action === this.mediaRowActions.rowSelected) {
        this.handleRowSelection({ action, audio, indx, eventDom })

        if (this.selectedMediaRowUIDs.length) {
          this.audioPlayerData = [audio]
          const updateAudioPlayer = this.$utils.clone(this.updateAudioPlayer)
          updateAudioPlayer.ref = this.$utils.generateUniqueID()
          updateAudioPlayer.action = this.mediaRowActions.updateAudioPreviewCurrentSong
          updateAudioPlayer.data = audio
          this.updateAudioPlayer = updateAudioPlayer
          this.projectPreview = { isPlaying: false, currentSong: audio }
        } else {
          // Reset project audio preview if currently playing, else keep playing the other song
          this.audioPlayerData = []
          this.resetProjectPreview()
        }

        if (this.projectFrameStatus === this.projectFrameStatusOptions.PLAY_FRAME) {
          if (!audio.audioPreview || (audio.audioPreview && !audio.audioPreview.url)) {
            return
          }
        }
      }

      /* Duplicate Row */
      if (action === this.mediaRowActions.duplicateRow) {
        this.setIsPlaylistChanged(true)
        const newMedia = this.$utils.clone(audio)

        if (newMedia.order) delete newMedia.order
        if (newMedia.uid) delete newMedia.uid
        delete newMedia._edit

        const projectAudioPlaylist = this.$utils.clone(this.projectAudioPlaylist)
        let firstGroup = []
        let lastGroup = []
        const sliceIndx = (indx === 0) ? 1 : indx

        firstGroup = projectAudioPlaylist.data.audio.slice(0, sliceIndx)
        const newUID = `${this.$utils.generateUniqueID()}-${firstGroup.length + 1}`
        newMedia.uid = newUID
        firstGroup.push(newMedia)
        lastGroup = projectAudioPlaylist.data.audio.slice(sliceIndx)

        projectAudioPlaylist.data.audio = firstGroup.concat(lastGroup)
        this.projectAudioPlaylist = this.filterProjectAudioPlaylistObject(projectAudioPlaylist)
      }

      /* Delete Row */
      if (action === this.mediaRowActions.deleteRow) {
        this.setIsPlaylistChanged(true)
        const projectAudioPlaylist = this.$utils.clone(this.projectAudioPlaylist)
        projectAudioPlaylist.data.audio.splice(indx, 1)

        // Delete selected row
        if (this.selectedMediaRowUIDs.length) {
          this.selectedMediaRowUIDs.forEach((uid) => {
            const findCurrentIndxInList = projectAudioPlaylist.data.audio.findIndex((item) => { return item.uid === uid })
            if (findCurrentIndxInList >= 0) {
              projectAudioPlaylist.data.audio.splice(findCurrentIndxInList, 1)
            }
          })
        }
        this.projectAudioPlaylist = this.filterProjectAudioPlaylistObject(projectAudioPlaylist)

        // Delete in uploading media
        if (this.uploadingMedia) {
          this.uploadingMedia = this.uploadingMedia.filter((resourceId) => { return resourceId !== audio.resourceId })
        }
      }

      if (action === this.mediaRowActions.playAudioPreview) {
        if (this.projectFrameStatus === this.projectFrameStatusOptions.EDIT_FRAME) {
          this.audioPlayerData = [audio]
        }

        const updateAudioPlayer = this.$utils.clone(this.updateAudioPlayer)
        updateAudioPlayer.ref = this.$utils.generateUniqueID()
        updateAudioPlayer.action = this.mediaRowActions.playAudioPreview
        updateAudioPlayer.data = audio
        this.updateAudioPlayer = updateAudioPlayer
      }

      if (action === this.mediaRowActions.pauseAudioPreview) {
        if (this.projectFrameStatus === this.projectFrameStatusOptions.EDIT_FRAME) {
          this.audioPlayerData = [audio]
        }

        const updateAudioPlayer = this.$utils.clone(this.updateAudioPlayer)
        updateAudioPlayer.ref = this.$utils.generateUniqueID()
        updateAudioPlayer.action = this.mediaRowActions.pauseAudioPreview
        updateAudioPlayer.data = audio
        this.updateAudioPlayer = updateAudioPlayer
      }
    },
    handleRowSelection (params) {
      const isCTRLKeyPressed = (params.eventDom) ? params.eventDom.ctrlKey : false
      const isShiftKeyPressed = (params.eventDom) ? params.eventDom.shiftKey : false
      const selectedMediaRowUIDs = this.$utils.clone(this.selectedMediaRowUIDs)
      const currentRowDetails = params.audio ? params.audio : { _edit: { uid: null } }
      const currentRowIndx = (params.indx && params.indx >= 0) ? params.indx : -1
      const isSelectedRowExistInSelection = selectedMediaRowUIDs.findIndex((uid) => { return uid === currentRowDetails.uid }) >= 0

      const keepLastEventRowItemClicked = () => { this.previousRowSelected = { row: currentRowDetails, position: currentRowIndx } }
      const updateSelectionList = (type, row, currentIndx) => {
        let updateSelectedMediaRowUIDs = this.$utils.clone(this.selectedMediaRowUIDs)

        if (type === 'reset') {
          updateSelectedMediaRowUIDs = []
        }

        if (type === 'add') {
          if (row && row.uid) updateSelectedMediaRowUIDs.push(row.uid)
        } else if (type === 'delete') {
          const findIndx = updateSelectedMediaRowUIDs.findIndex((uid) => { return uid === row.uid })
          if (findIndx >= 0) updateSelectedMediaRowUIDs.splice(currentIndx, 1)
        }

        this.selectedMediaRowUIDs = updateSelectedMediaRowUIDs
        this.projectAudioPlaylist = this.filterProjectAudioPlaylistObject(this.projectAudioPlaylist)
      }

      // Single Selection, No Key pressed
      if (!isCTRLKeyPressed && !isShiftKeyPressed) {
        updateSelectionList('reset')

        if (['shift', 'ctrl'].indexOf(this.previousKeyPressedInRowSelection) >= 0) {
          updateSelectionList('add', currentRowDetails)
        } else {
          if (!isSelectedRowExistInSelection) {
            updateSelectionList('add', currentRowDetails)
          }
        }

        keepLastEventRowItemClicked()
        this.previousKeyPressedInRowSelection = null // Keep as previous key pressed
      }

      // Multiple Selection with CTRL Key Pressed
      if (isCTRLKeyPressed || (isCTRLKeyPressed && !isShiftKeyPressed)) {
        if (!isSelectedRowExistInSelection) {
          updateSelectionList('add', currentRowDetails)
        } else {
          updateSelectionList('delete', currentRowDetails, currentRowIndx)
        }

        keepLastEventRowItemClicked()
        this.previousKeyPressedInRowSelection = 'ctrl' // Keep as previous key pressed
      }

      // Multiple Selection with SHIFT Key Pressed
      if (isShiftKeyPressed) {
        const previousRowSelected = this.$utils.clone(this.previousRowSelected)
        const previousRowSelectedPosition = (previousRowSelected.position >= 0) ? previousRowSelected.position : -1

        updateSelectionList('reset')
        if (previousRowSelectedPosition < currentRowIndx) {
          for (let i = previousRowSelectedPosition; i <= currentRowIndx; i++) {
            updateSelectionList('add', this.projectAudioPlaylist.data.audio[i])
          }
        }

        if (previousRowSelectedPosition > currentRowIndx) {
          for (let i = currentRowIndx; i <= previousRowSelectedPosition; i++) {
            updateSelectionList('add', this.projectAudioPlaylist.data.audio[i])
          }
        }
        this.previousKeyPressedInRowSelection = 'shift' // Keep as previous key pressed
      }
    },
    handleAudioPlayerChange ($event) {
      if (['PLAY_DEFAULT_SONG', 'PLAY_NEXT_SONG', 'PLAY_CURRENT_SONG', 'PAUSE_CURRENT_SONG'].indexOf($event.action) >= 0) {
        const getAudioDetailsIndx = this.projectAudioPlaylist.data.audio.findIndex((audio) => { return audio.uid === $event.payload.data.currentSong.uid })
        if (getAudioDetailsIndx >= 0) {
          this.projectPreview = { isPlaying: $event.payload.data.isPlaying, currentSong: $event.payload.data.currentSong }
        } else {
          this.resetProjectPreview()
        }
      }

      if (['ENDED_SONG'].indexOf($event.action) >= 0) {
        this.resetProjectPreview()
      }

      if ($event.action === 'ENDED_SONG_PLAY_AND_SELECT_NEXT') {
        const getAudioDetailsIndx = this.projectAudioPlaylist.data.audio.findIndex((audio) => { return audio.uid === $event.payload.data.currentSong.uid })

        if (getAudioDetailsIndx >= 0) {
          this.projectPreview = { isPlaying: $event.payload.data.isPlaying, currentSong: $event.payload.data.currentSong }
          this.handleMediaRowClick(this.mediaRowActions.rowSelected, this.projectAudioPlaylist.data.audio[getAudioDetailsIndx], getAudioDetailsIndx, {})
          this.handleMediaRowClick(this.mediaRowActions.playAudioPreview, this.projectAudioPlaylist.data.audio[getAudioDetailsIndx], getAudioDetailsIndx, {})
        } else {
          this.resetProjectPreview()
        }
      }

      if ($event.action === 'ENDED_ALL_SONGS') {
        this.resetProjectPreview()
      }

      if (['CURRENT_SONG_UPDATED'].indexOf($event.action) >= 0) {
        this.projectPreview = { isPlaying: $event.payload.data.isPlaying, currentSong: $event.payload.data.currentSong, standBy: true }
      }

      if (['NO_CURRENT_SONG_TO_PLAY'].indexOf($event.action) >= 0) {
        this.resetProjectPreview()
      }

      if (['PLAY_NEW_SELECTED_SONG'].indexOf($event.action) >= 0) {
        const getAudioDetailsIndx = this.projectAudioPlaylist.data.audio.findIndex((audio) => { return audio.uid === $event.payload.data.uid })

        if (getAudioDetailsIndx >= 0) {
          this.handleMediaRowClick(this.mediaRowActions.playAudioPreview, this.projectAudioPlaylist.data.audio[getAudioDetailsIndx], getAudioDetailsIndx, {})
        } else {
          this.handleMediaRowClick(this.mediaRowActions.playAudioPreview, this.projectAudioPlaylist.data.audio[0], 0, {})
        }
      }
    },
    resetProjectPreview () {
      this.projectPreview = { isPlaying: false, currentSong: {} }
    },
    resetAudioPlayerAction () {
      const updateAudioPlayer = this.$utils.clone(this.updateAudioPlayer)
      updateAudioPlayer.ref = this.$utils.generateUniqueID()
      updateAudioPlayer.action = this.mediaRowActions.resetAudioPlayer
      updateAudioPlayer.data = { submitPlaylistToAPI: true }
      this.updateAudioPlayer = updateAudioPlayer
      this.projectPreview = { isPlaying: false, currentSong: {} }
    },
    handleProjectAudioMenuChange ($event) {
      this.setIsPlaylistChanged(true)
      if ($event.action === 'TAGS_CHANGED') {
        const projectAudioPlaylist = this.$utils.clone(this.projectAudioPlaylist)
        projectAudioPlaylist.tags = $event.payload.data
        this.projectAudioPlaylist = projectAudioPlaylist
      }

      if ($event.action === 'NAME_CHANGED') {
        const projectAudioPlaylist = this.$utils.clone(this.projectAudioPlaylist)
        projectAudioPlaylist.name = $event.payload.data
        this.projectAudioPlaylist = projectAudioPlaylist
      }
    },
    /**
     * Hook function if media menu/properties sub-component will change or throw an action
     * @param {Object} event - event deatils
     */
    menuMediaDetailsOnChange (event) {
      if (event.action === 'UPDATE_PROJECT_TEMPLATE_MEDIA_DATA') {
        this.projectMediaDetails = event.payload.data
        // Set Unsaved Changes
      }
    },
    handleRowItemMouseHoverAndMouseOut (mouseEvent, item, domEvent) {
      if (domEvent.path && $(domEvent.path).hasClass('remove')) {
        if (mouseEvent === 'mouseover') {
          this.hoverAllDeleteIcon = true
        } else {
          this.hoverAllDeleteIcon = false
        }
      }
    },
    addMessageListener () {
      if (!this.handleSVGMessageEventListenerFunction) {
        this.handleSVGMessageEventListenerFunction = this.handleSVGMessage.bind(this)
        window.addEventListener('message', this.handleSVGMessageEventListenerFunction)
      }
    },
    removeMessageListener () {
      if (this.handleSVGMessageEventListenerFunction) {
        window.removeEventListener('message', this.handleSVGMessageEventListenerFunction)
        this.handleSVGMessageEventListenerFunction = null
      }
    },
    handleSVGMessage (event) {
      if (this.projectFrameStatus === this.projectFrameStatusOptions.EDIT_FRAME) {
        // Make sure event.origin is from the app to prevent accepting message out of nowhere and event.data is an object
        const sessionStage = useCognitoAuthStore().sessionStage ?? CONFIG.GLOBAL_STAGE;
        const apiOrigin = (new URL(this.$utils.getStageAPIUrl(sessionStage))).origin
        if ((event.origin !== apiOrigin) || (!_.isPlainObject(event.data))) return

        if (event.data.status && event.data.status === 'finished') {
          const newMediaDetails = event.data.media
          const projectAudioPlaylist = this.$utils.clone(this.projectAudioPlaylist)

          // Update Audio details
          projectAudioPlaylist.data.audio.forEach((audio, indx) => {
            if (audio.resourceId === newMediaDetails.resourceId) {
              projectAudioPlaylist.data.audio[indx].audioPreview = newMediaDetails.audioPreview ? newMediaDetails.audioPreview : ''
              projectAudioPlaylist.data.audio[indx].preview = newMediaDetails.preview ? newMediaDetails.preview : {}
              projectAudioPlaylist.data.audio[indx].dur = newMediaDetails.dur ? newMediaDetails.dur : 0

              if (projectAudioPlaylist.data.audio[indx].status) delete projectAudioPlaylist.data.audio[indx].status
            }
          })

          this.projectAudioPlaylist = this.filterProjectAudioPlaylistObject(projectAudioPlaylist)

          const findIndxFromUploadingMedia = this.uploadingMedia.findIndex((item) => { return item === newMediaDetails.resourceId })
          if (findIndxFromUploadingMedia >= 0) {
            this.uploadingMedia.splice(findIndxFromUploadingMedia, 1)
          }
        }
      }
    },
    setIsPlaylistChanged (changed) {
      if (changed) {
        this.setUnsavedDataFlagIntoLocalstorage({ page: 'projectAudio' })
      } else {
        this.removeUnsavedDataFlagInLocalStorage()
      }
    }
  },
  beforeUnmount () {
    this.removeMessageListener()
  },
  mounted () {
    this.addMessageListener()
    this.isAuthenticated({
      callback: () => {
        this.setDefaultData()
        this.handleSubmitButtonText(this.projectFrameStatus)
      }
    })
  },
  beforeRouteLeave (_to, _from, next) {
    this.checkUnsavedData({ nextAction: () => next() })
  }
}
